import * as selectGroupedTypes from "./types";
import * as validationLib from "../../lib/validation";
import ChevronDown from "../icons/ChevronDown";
import cn from "../../lib/classnames";
import GreenCheck from "../icons/GreenCheck";
import React, {useRef, useCallback, useState, useEffect} from "react";
import RedX from "../icons/RedX";
import styles from "./SelectGrouped.module.css";
import textStyles from "../../styles/Text.module.css";

export const EMPTY_VALUE = "";

type Props = React.ComponentProps<"select"> & {
    className?: string;
    extraInfo?: string;
    id: string;
    label: string;
    optionGroups: selectGroupedTypes.OptionGroup[];
    selectedId: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    validationMessage?: string[];
    useLargeChevron?: boolean;
};

function SelectGrouped(
    {
        id,
        label,
        optionGroups,
        selectedId,
        onChange,
        validationMessage,
        extraInfo,
        onBlur,
        className,
        useLargeChevron,
        ...rest
    }: Props,
    ref: React.ForwardedRef<HTMLSelectElement>,
) {
    const [infoCollapsed, setInfoCollapsed] = useState(true);

    const containerRef = useRef<HTMLDivElement>(null);
    const borderBottomRef = useRef<HTMLDivElement>(null);

    const hasValidationMsg = !!validationMessage && !!validationMessage.length;

    const showValidIcon = useCallback(() => {
        return !hasValidationMsg && !!selectedId;
    }, [selectedId, hasValidationMsg]);

    useEffect(() => {
        if (!borderBottomRef.current || !containerRef.current) {
            return;
        }

        if (hasValidationMsg || showValidIcon()) {
            const fullWidth = containerRef.current.getBoundingClientRect().width;
            const scale = (fullWidth - 40) / fullWidth;
            borderBottomRef.current.style.transform = `scaleX(${scale})`;
        } else {
            borderBottomRef.current.style.transform = "scaleX(1)";
        }
    }, [showValidIcon, hasValidationMsg]);

    return (
        <div ref={containerRef} className={cn(className, styles.container)}>
            <div className={styles.selectContainer}>
                <select
                    ref={ref}
                    id={id}
                    className={cn(
                        styles.select,
                        textStyles.input,
                        selectedId ? undefined : styles.noneSelected,
                        hasValidationMsg || showValidIcon() ? styles.selectWithIcon : undefined,
                    )}
                    value={selectedId}
                    onChange={onChange}
                    onFocus={() => setInfoCollapsed(false)}
                    onBlur={(e) => {
                        setInfoCollapsed(true);
                        if (onBlur) {
                            onBlur(e);
                        }
                    }}
                    {...rest}
                >
                    {label ? (
                        <option value={EMPTY_VALUE} disabled={true}>
                            {label}
                        </option>
                    ) : null}
                    {optionGroups.map((group) => (
                        <optgroup label={group.label} key={group.id}>
                            {group.options.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
                <label
                    className={cn(styles.label, selectedId ? undefined : styles.empty)}
                    htmlFor={id}
                    data-content={label}
                >
                    <span className={styles.hidden}>{label}</span>
                </label>
                <ChevronDown
                    className={cn(
                        styles.chevron,
                        useLargeChevron ? styles.largeChevron : undefined,
                        selectedId ? undefined : styles.empty,
                        hasValidationMsg ? styles.invalid : undefined,
                    )}
                />
                <RedX
                    className={cn(
                        styles.validityIcon,
                        hasValidationMsg ? styles.showInvalidIcon : undefined,
                    )}
                />
                <GreenCheck
                    className={cn(
                        styles.validityIcon,
                        showValidIcon() ? styles.showValidIcon : undefined,
                    )}
                />
                <div
                    ref={borderBottomRef}
                    className={cn(
                        styles.bottomBorder,
                        hasValidationMsg ? styles.invalid : undefined,
                    )}
                />
            </div>

            {validationMessage !== undefined && validationMessage.length ? (
                <div className={styles.validation}>
                    {validationLib.getDisplayString(validationMessage)}
                </div>
            ) : null}
            {extraInfo ? (
                <div
                    className={cn(
                        textStyles.inputDetail,
                        styles.extraInfo,
                        infoCollapsed ? styles.collapsed : styles.expanded,
                    )}
                >
                    {extraInfo}
                </div>
            ) : null}
        </div>
    );
}

export default React.forwardRef<HTMLSelectElement, Props>(SelectGrouped);
